import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import BlogSidebar from '../components/sidebars/BlogSidebar';
import AdSlot from '../components/AdSlot';
import { Button } from '../components/ui/button';
import postsData from '../data/posts.json';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const PostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = postsData.find((p: any) => p.slug === slug);

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
        <Link to="/blog">
          <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white">
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  const postData = post as any;

  return (
    <>
      <SEO
        title={postData.title}
        description={postData.excerpt}
        image={postData.featuredImage}
        type="article"
        schemaType="blog"
      />
      <BreadcrumbSchema
  items={[
    { name: "Home", url: "https://www.thecodebazaar.com/" },
    { name: "Blog", url: "https://www.thecodebazaar.com/blog" },
    {
      name: post.title,
      url: `https://www.thecodebazaar.com/blog/${post.slug}`,
    },
  ]}
/>


      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <BlogSidebar />
          </div>

          <div className="lg:col-span-3">
            <Link to="/blog" className="inline-flex items-center text-sky-500 hover:text-sky-600 mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            <article className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
              {/* Featured Image */}
              <img
                src={postData.featuredImage}
                alt={postData.title}
                className="w-full h-96 object-cover"
              />

              <div className="p-8 md:p-12">
                {/* Meta */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="px-4 py-1 bg-sky-50 text-sky-600 text-sm rounded-full font-medium">
                    {postData.category}
                  </span>
                  {postData.tags?.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-4 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Title */}
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {postData.title}
                </h1>

                {/* Author & Date */}
                <div className="flex items-center space-x-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>{postData.author}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5" />
                    <span>{postData.date}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="prose max-w-none mb-8">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {postData.content}
                  </p>
                </div>

                {/* Custom HTML */}
                {postData.customHtml && (
                  <div
                    className="prose max-w-none mb-8 p-6 bg-sky-50 border border-sky-200 rounded-xl"
                    dangerouslySetInnerHTML={{ __html: postData.customHtml }}
                  />
                )}

                <AdSlot slot="heroBanner" className="mb-10" />


                {/* Tags */}
                <div className="pt-8 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags:</h3>
                  <div className="flex flex-wrap gap-2">
                    {postData.tags?.map((tag: string) => (
                      <Link
                        key={tag}
                        to={`/blog?tag=${tag}`}
                        className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-sky-50 hover:text-sky-600 transition-colors"
                      >
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            {/* Related Posts CTA */}
            <div className="mt-8 bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-8 border border-sky-200 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Want to Read More?
              </h3>
              <p className="text-gray-600 mb-6">
                Explore more articles and tutorials on our blog
              </p>
              <Link to="/blog">
                <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                  View All Posts
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PostDetail;